import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/core/store";
import { useModalRouter } from "@/hooks/useModalRouter";
import { useOrderQuery, useLastDraftOrder } from "@/core/orders/hooks";
import {
  loadOrderSuccess,
  loadLastDraftOrderSuccess,
  detachOrder,
} from "@/core/orders/reducer";
import { isOrderId, isOrderNotFound } from "@/core/orders/guards";
import { selectCurrentOrder } from "@/core/orders/selectors";
import {
  selectBrandId,
  selectPointId,
  selectPendingEditOrderId,
  selectIsReadOnly,
} from "@/core/app/selectors";
import { setPendingEditOrderId } from "@/core/app/reducer";

/**
 * Наполнение корзины заказом с сервера. Пишется впервые: прежняя цепочка
 * запускалась только из 404-ветки саги, а DataLoader вдобавок стирал
 * persistedOrderId на каждом рендере.
 *
 * Два сценария, один приёмник:
 *   - редактирование: хост прислал EDIT_ORDER с orderId;
 *   - восстановление: пользователь вернулся, последний черновик точки
 *     поднимается списочным запросом.
 *
 * Приоритет у EDIT_ORDER — он адресный и пришёл от хоста. Оба сценария пишут
 * в один currentOrder, и без приоритета выигрывал бы тот, чей ответ приехал
 * позже.
 *
 * Монтируется РОВНО ОДИН раз на приложение (CartSyncProvider): второй
 * экземпляр наполнял бы корзину повторно.
 */
export function useOrderRestore(): void {
  const dispatch = useAppDispatch();
  const { navigateToModal } = useModalRouter();

  const brandId = useAppSelector(selectBrandId);
  const pointId = useAppSelector(selectPointId);
  const pendingEditOrderId = useAppSelector(selectPendingEditOrderId);
  const isReadOnly = useAppSelector(selectIsReadOnly);
  const currentOrderId = useAppSelector(selectCurrentOrder).id;

  // Негодный uuid запроса не порождает и задание снимает: это мусор в
  // интеграции, а не ошибка пользователя — снекбара нет.
  const editOrderId = isOrderId(pendingEditOrderId) ? pendingEditOrderId : null;
  useEffect(() => {
    if (pendingEditOrderId && !isOrderId(pendingEditOrderId)) {
      dispatch(setPendingEditOrderId(null));
    }
  }, [pendingEditOrderId, dispatch]);

  const editQuery = useOrderQuery(editOrderId, brandId);
  // Восстановление гасится, пока живо задание на редактирование.
  const draftQuery = useLastDraftOrder(
    editOrderId ? null : brandId,
    editOrderId ? null : pointId
  );

  // Наполнение — один раз на orderId: мутации инвалидируют /orders/{id},
  // запрос перезапрашивается, и без гарда ответ затирал бы правку, сделанную
  // после отправки.
  const appliedRef = useRef<string | null>(null);
  // Открытие модалки считается отдельно от наполнения: черновик и
  // адресованный EDIT_ORDER — сплошь и рядом ОДИН заказ, и общий гард молча
  // съедал бы открытие, которого хост ждёт.
  const openedForRef = useRef<string | null>(null);

  useEffect(() => {
    const order = editQuery.data;
    if (!order) return;

    if (appliedRef.current !== order.id) {
      appliedRef.current = order.id;
      dispatch(loadOrderSuccess(order));
    }

    // pendingEditOrderId после успеха НЕ снимается: он и есть признак «правим
    // адресованный заказ». Сняли бы — восстановление тут же подняло бы
    // последний черновик точки и затёрло им корзину.

    // Модалка открывается только в этой ветке: на восстановлении черновика
    // виджет прыгал бы в корзину при каждом возврате.
    if (!isReadOnly && openedForRef.current !== order.id) {
      openedForRef.current = order.id;
      navigateToModal("order");
    }
  }, [editQuery.data, isReadOnly, navigateToModal, dispatch]);

  // 404 — «нет или чужой», различить нельзя намеренно. Уходим в восстановление
  // черновика, сняв задание.
  useEffect(() => {
    if (!isOrderNotFound(editQuery.error)) return;

    // Ссылку снимаем ТОЛЬКО если мёртв именно тот заказ, что лежит в корзине:
    // пока чужой id ехал за своим 404, фоновая синхронизация могла завести
    // здоровый черновик, и detachOrder оставил бы его сиротой, а следующая
    // правка создала бы второй заказ тем же составом.
    if (currentOrderId && currentOrderId === editOrderId) {
      dispatch(detachOrder());
    }
    dispatch(setPendingEditOrderId(null));
  }, [editQuery.error, currentOrderId, editOrderId, dispatch]);

  useEffect(() => {
    const draft = draftQuery.data?.items?.[0];
    if (!draft || appliedRef.current === draft.id) return;

    appliedRef.current = draft.id;
    dispatch(loadLastDraftOrderSuccess(draft));
  }, [draftQuery.data, dispatch]);
}

export default useOrderRestore;
